import { supabase } from './supabase'
import { adminSupabase, isAdminAuthenticated } from './adminSupabase'
import { Product, Category } from '../types'

// Admin işlemleri için doğru client'ı seç
const getSupabaseClient = async () => {
  return (await isAdminAuthenticated()) ? adminSupabase : supabase
}

/**
 * Tüm kategorileri getir
 */
export async function getCategories(): Promise<Category[]> {
  const client = await getSupabaseClient()
  const { data, error } = await client
    .from('categories')
    .select('*')
    .order('name')

  if (error) {
    console.error('Kategoriler getirilirken hata:', error)
    throw error
  }

  return (data as Category[]) || []
}

/**
 * Tüm ürünleri getir (admin için)
 */
export async function getProducts(): Promise<Product[]> {
  const client = await getSupabaseClient()
  const { data, error } = await client
    .from('products')
    .select(`
      *,
      category:categories (
        id,
        name,
        slug
      )
    `)
    .order('name')

  if (error) {
    console.error('Ürünler getirilirken hata:', error)
    throw error
  }

  // DB'den gelen snake_case verilerini camelCase'e çevir
  const mappedData = data?.map(item => ({
    id: item.id,
    name: item.name,
    description: item.description,
    price: item.price,
    originalPrice: item.original_price,
    image: item.image_url,
    category: item.category,
    isActive: item.is_active,
    isFeatured: item.is_featured,
    isBestseller: item.is_bestseller,
    isDiscounted: item.is_discounted,
    isNew: item.is_new,
    rating: item.rating,
    stock: item.stock || 100, // Default stock value
    created_at: item.created_at,
    updated_at: item.updated_at
  })) || []

  return mappedData as unknown as Product[]
}

/**
 * Sadece aktif ürünleri getir (public için)
 */
export async function getActiveProducts(): Promise<Product[]> {
  const { data, error } = await supabase
    .from('products')
    .select(`
      *,
      category:categories (
        id,
        name,
        slug
      )
    `)
    .eq('is_active', true)
    .order('name')

  if (error) {
    console.error('Aktif ürünler getirilirken hata:', error)
    throw error
  }

  // DB'den gelen snake_case verilerini camelCase'e çevir
  const mappedData = data?.map(item => ({
    id: item.id,
    name: item.name,
    description: item.description,
    price: item.price,
    originalPrice: item.original_price,
    image: item.image_url,
    category: item.category,
    isActive: item.is_active,
    isFeatured: item.is_featured,
    isBestseller: item.is_bestseller,
    isDiscounted: item.is_discounted,
    isNew: item.is_new,
    rating: item.rating,
    stock: item.stock || 100, // Default stock value
    created_at: item.created_at,
    updated_at: item.updated_at
  })) || []

  return mappedData as unknown as Product[]
}

/**
 * Yeni ürün ekle
 */
export async function createProduct(productData: Omit<Product, 'id' | 'created_at' | 'updated_at'>): Promise<Product> {
  // Önce ürünü ekle
  const client = await getSupabaseClient()
  const { data: insertData, error: insertError } = await client
    .from('products')
    .insert([{
      name: productData.name,
      description: productData.description,
      price: productData.price,
      original_price: productData.originalPrice,
      image_url: productData.image,
      category_id: productData.category,
      is_active: productData.isActive,
      is_featured: productData.isFeatured,
      is_bestseller: productData.isBestseller || false,
      is_discounted: productData.isDiscounted || false,
      is_new: productData.isNew || false
    }])
    .select('id')
    .single()

  if (insertError) {
    console.error('Ürün eklenirken hata:', insertError)
    throw insertError
  }

  // Sonra eklenen ürünü kategori bilgisiyle birlikte getir
  const { data, error } = await client
    .from('products')
    .select(`
      *,
      category:categories (
        id,
        name,
        slug
      )
    `)
    .eq('id', (insertData as any).id)
    .single()

  if (error) {
    console.error('Eklenen ürün getirilirken hata:', error)
    throw error
  }

  // DB'den gelen snake_case verilerini camelCase'e çevir
  const mappedData = {
    id: data.id,
    name: data.name,
    description: data.description,
    price: data.price,
    originalPrice: data.original_price,
    image: data.image_url,
    category: data.category,
    isActive: data.is_active,
    isFeatured: data.is_featured,
    isBestseller: data.is_bestseller,
    isDiscounted: data.is_discounted,
    isNew: data.is_new,
    rating: data.rating,
    stock: data.stock || 100, // Default stock value
    created_at: data.created_at,
    updated_at: data.updated_at
  }

  return mappedData as unknown as Product
}

/**
 * Ürün güncelle
 */
export async function updateProduct(id: string, productData: Partial<Product>): Promise<Product> {
  // Önce ürünü güncelle
  const client = await getSupabaseClient()
  const { error: updateError } = await client
    .from('products')
    .update({
      name: productData.name,
      description: productData.description,
      price: productData.price,
      original_price: productData.originalPrice,
      image_url: productData.image,
      category_id: productData.category,
      is_active: productData.isActive,
      is_featured: productData.isFeatured,
      is_bestseller: productData.isBestseller,
      is_discounted: productData.isDiscounted,
      is_new: productData.isNew,
      updated_at: new Date().toISOString()
    })
    .eq('id', id)

  if (updateError) {
    console.error('Ürün güncellenirken hata:', updateError)
    throw updateError
  }

  // Sonra güncellenmiş ürünü kategori bilgisiyle birlikte getir
  const { data, error } = await client
    .from('products')
    .select(`
      *,
      category:categories (
        id,
        name,
        slug
      )
    `)
    .eq('id', id)
    .single()

  if (error) {
    console.error('Güncellenmiş ürün getirilirken hata:', error)
    throw error
  }

  // DB'den gelen snake_case verilerini camelCase'e çevir
  const mappedData = {
    id: data.id,
    name: data.name,
    description: data.description,
    price: data.price,
    originalPrice: data.original_price,
    image: data.image_url,
    category: data.category,
    isActive: data.is_active,
    isFeatured: data.is_featured,
    isBestseller: data.is_bestseller,
    isDiscounted: data.is_discounted,
    isNew: data.is_new,
    rating: data.rating,
    stock: data.stock || 100, // Default stock value
    created_at: data.created_at,
    updated_at: data.updated_at
  }

  return mappedData as unknown as Product
}

/**
 * Ürün sil
 */
export async function deleteProduct(id: string): Promise<void> {
  const client = await getSupabaseClient()
  const { error } = await client
    .from('products')
    .delete()
    .eq('id', id)

  if (error) {
    console.error('Ürün silinirken hata:', error)
    throw error
  }
}

/**
 * Kategoriye göre ürünleri getir
 */
export async function getProductsByCategory(categoryId: string): Promise<Product[]> {
  const { data, error } = await supabase
    .from('products')
    .select(`
      *,
      category:categories (
        id,
        name,
        slug
      )
    `)
    .eq('category_id', categoryId)
    .eq('is_active', true)
    .order('name')

  if (error) {
    console.error('Kategoriye göre ürünler getirilirken hata:', error)
    throw error
  }

  // DB'den gelen snake_case verilerini camelCase'e çevir
  const mappedData = data?.map(item => ({
    id: item.id,
    name: item.name,
    description: item.description,
    price: item.price,
    originalPrice: item.original_price,
    image: item.image_url,
    category: item.category,
    isActive: item.is_active,
    isFeatured: item.is_featured,
    isBestseller: item.is_bestseller,
    isDiscounted: item.is_discounted,
    isNew: item.is_new,
    rating: item.rating,
    stock: item.stock || 100, // Default stock value
    created_at: item.created_at,
    updated_at: item.updated_at
  })) || []

  return mappedData as unknown as Product[]
}

/**
 * Öne çıkan ürünleri getir
 */
export async function getFeaturedProducts(): Promise<Product[]> {
  const { data, error } = await supabase
    .from('products')
    .select(`
      *,
      category:categories (
        id,
        name,
        slug
      )
    `)
    .eq('is_featured', true)
    .eq('is_active', true)
    .order('name')
    .limit(6)

  if (error) {
    console.error('Öne çıkan ürünler getirilirken hata:', error)
    throw error
  }

  // DB'den gelen snake_case verilerini camelCase'e çevir
  const mappedData = data?.map(item => ({
    id: item.id,
    name: item.name,
    description: item.description,
    price: item.price,
    originalPrice: item.original_price,
    image: item.image_url,
    category: item.category,
    isActive: item.is_active,
    isFeatured: item.is_featured,
    isBestseller: item.is_bestseller,
    isDiscounted: item.is_discounted,
    isNew: item.is_new,
    rating: item.rating,
    stock: item.stock || 100, // Default stock value
    created_at: item.created_at,
    updated_at: item.updated_at
  })) || []

  return mappedData as unknown as Product[]
}

/**
 * Ürün arama
 */
export async function searchProducts(query: string): Promise<Product[]> {
  const { data, error } = await supabase
    .from('products')
    .select(`
      *,
      category:categories (
        id,
        name,
        slug
      )
    `)
    .or(`name.ilike.%${query}%,description.ilike.%${query}%`)
    .eq('is_active', true)
    .order('name')

  if (error) {
    console.error('Ürün arama hatası:', error)
    throw error
  }

  // DB'den gelen snake_case verilerini camelCase'e çevir
  const mappedData = data?.map(item => ({
    id: item.id,
    name: item.name,
    description: item.description,
    price: item.price,
    originalPrice: item.original_price,
    image: item.image_url,
    category: item.category,
    isActive: item.is_active,
    isFeatured: item.is_featured,
    isBestseller: item.is_bestseller,
    isDiscounted: item.is_discounted,
    isNew: item.is_new,
    rating: item.rating,
    stock: item.stock || 100, // Default stock value
    created_at: item.created_at,
    updated_at: item.updated_at
  })) || []

  return mappedData as unknown as Product[]
}

/**
 * Tek ürün getir
 */
export async function getProduct(id: string): Promise<Product | null> {
  const { data, error } = await supabase
    .from('products')
    .select(`
      *,
      category:categories (
        id,
        name,
        slug
      )
    `)
    .eq('id', id)
    .eq('is_active', true)
    .single()

  if (error) {
    console.error('Ürün getirilirken hata:', error)
    return null
  }

  if (!data) return null

  // DB'den gelen snake_case verilerini camelCase'e çevir
  const mappedData = {
    id: data.id,
    name: data.name,
    description: data.description,
    price: data.price,
    originalPrice: data.original_price,
    image: data.image_url,
    category: data.category,
    isActive: data.is_active,
    isFeatured: data.is_featured,
    isBestseller: data.is_bestseller,
    isDiscounted: data.is_discounted,
    isNew: data.is_new,
    rating: data.rating,
    stock: data.stock || 100, // Default stock value
    created_at: data.created_at,
    updated_at: data.updated_at
  }

  return mappedData as unknown as Product
}